import { FormattedMessage } from "react-intl";
import { useNavigate, useParams } from "react-router-dom";
import moment from "moment";

import { useGetShipmentDetails } from "api/queries/shipments/useGetShipmentDetails";
import { ReactComponent as ClockOrange } from "assets/images/iconsV2/clock-orange.svg";
import { ReactComponent as CancelIcon } from "assets/images/iconsV2/no-entry.svg";
import { ReactComponent as ArchivedIcon } from "assets/images/iconsV2/archived.svg";
import { ReactComponent as CheckIcon } from "assets/images/iconsV2/check-circle-green.svg";
import usePermissions from "hooks/usePermissions";

interface QuotationFooterStatusProps {
  quotation: any;
}

export default function QuotationFooterStatus({ quotation }: QuotationFooterStatusProps) {
  const { id } = useParams();
  const navigate = useNavigate();

  const { permissions } = usePermissions();
  const { user_type } = JSON.parse(permissions || "{}");
  const isShipper = user_type === "shipper";
  const isProvider = user_type === "provider";

  const { general, status } = quotation;

  const isPendingApproval = isShipper && ["require_approval"].includes(status);
  const isPendingExtension =
    !["booked", "booked_as_duplicate", "canceled", "canceled_by_provider", "lost"].includes(status) &&
    general?.extension_requested_at &&
    general?.extension_requested_by &&
    isProvider;
  const isPendingRevalidation = ["confirmation_needed"].includes(status);
  const isCancelled = ["canceled", "canceled_by_provider"].includes(status);
  const isArchived = ["lost"].includes(status);

  // Booked info
  const isBooked = ["booked", "booked_as_duplicate"].includes(status) || general?.booked_as_duplicate_shipment_id;
  const { data: shipmentDetailsData = {} } = useGetShipmentDetails(id!, true, {
    enabled: ["booked", "booked_as_duplicate"].includes(status),
    refetchOnMount: false,
    hideError: true,
  });
  const bookingDate = shipmentDetailsData?.schedule?.booking_date;

  // TBQR info
  const duplicateBookedShipmentId = general?.booked_as_duplicate_shipment_id;
  const duplicateBookedDate = general?.booked_as_duplicate_date;
  const isBookedAsDuplicate = duplicateBookedShipmentId && duplicateBookedDate;

  return (
    <div className="d-flex align-items-center gap-sm text-sm font-weight-semibold ml-xxs">
      {isBookedAsDuplicate ? (
        <>
          <CheckIcon height={18} width={18} />
          <div className="quotation-booked-info">
            <FormattedMessage id="general.quote.bookedOn" defaultMessage="Booked on" />{" "}
            {moment(duplicateBookedDate).format("DD MMM YYYY")},{" "}
            <span
              className="text-brand-secondary cursor-pointer"
              onClick={() => navigate(`/shipment/${duplicateBookedShipmentId}/tracking`)}
            >
              {duplicateBookedShipmentId}
            </span>
          </div>
        </>
      ) : isPendingApproval ? (
        <>
          <ClockOrange height={18} width={18} />
          <FormattedMessage id="general.pendingApproval" defaultMessage="Pending approval" />
        </>
      ) : isPendingExtension ? (
        <>
          <ClockOrange height={18} width={18} />
          <FormattedMessage id="quote.quotation.pendingExtension" defaultMessage="Pending extension" />
        </>
      ) : isPendingRevalidation ? (
        <>
          <ClockOrange height={18} width={18} />
          {isProvider ? (
            <FormattedMessage id="quote.quotation.pendingRevalidation" defaultMessage="Pending revalidation" />
          ) : (
            <FormattedMessage
              id="shipment-quotes.components.quotes.pendingProviderRevalidation"
              defaultMessage="Pending provider revalidation"
            />
          )}
        </>
      ) : isCancelled ? (
        <>
          <CancelIcon height={18} width={18} />
          <FormattedMessage id="general.canceled" defaultMessage="Canceled" />
        </>
      ) : isArchived ? (
        <>
          <ArchivedIcon height={18} width={18} />
          <FormattedMessage id="general.archived" defaultMessage="Archived" />
        </>
      ) : isBooked ? (
        <>
          <CheckIcon height={18} width={18} />
          <div className="quotation-booked-info">
            <FormattedMessage id="general.quote.bookedOn" defaultMessage="Booked on" />{" "}
            {moment(bookingDate).format("DD MMM YYYY")}
          </div>
        </>
      ) : null}
    </div>
  );
}
